// @flow
import config from '../../config';
import { APIError, makeAPICall, redirectTo } from '../generalCreators';
import { updateCurrentCampaign } from './currentCampaignCreators';

export function getCampaigns(
  organisationId: number,
  excludeArchived: boolean = false
): any {
  return function(dispatch, getState) {
    const endpoint = '/campaigns';
    const options = {
      method: 'GET',
      headers: { token: getState().authentication.token },
      params: {
        productId: config.productId,
        organisationId,
        excludeArchived
      }
    };
    return makeAPICall(endpoint, options).then(
      data =>
        dispatch({ type: 'UPDATE_CAMPAIGNS', campaigns: data.data.campaigns }),
      error => dispatch(APIError(error, 'Error getting Campaigns'))
    );
  };
}

export function getCampaignsWithDetail(
  organisationId: number,
  excludeArchived: boolean = false
): any {
  return function(dispatch, getState) {
    console.log(getState().authentication.token);
    const endpoint = '/campaigns-with-detail';
    const options = {
      method: 'GET',
      headers: { token: getState().authentication.token },
      params: {
        productId: config.productId,
        organisationId,
        excludeArchived
      }
    };
    return makeAPICall(endpoint, options).then(
      data =>
        dispatch({ type: 'UPDATE_CAMPAIGNS', campaigns: data.data.campaigns }),
      error => dispatch(APIError(error, 'Error getting Campaigns'))
    );
  };
}

export function getCampaignAddressesCount(
  campaignId: number,
  organisationId: number,
  limit: ?number
): any {
  return function(dispatch, getState) {
    const endpoint = `/campaign/${campaignId}/getCampaignAddressesCount`;
    const options = {
      method: 'GET',
      headers: { token: getState().authentication.token },
      params: {
        productId: config.productId,
        organisationId,
        limit
      }
    };
    return makeAPICall(endpoint, options).then(
      data =>
        dispatch({
          type: 'UPDATE_CAMPAIGNS_DELIVERY_POINTS',
          deliveryPoints: data.data
        }),
      error => dispatch(APIError(error, 'Error getting Campaigns'))
    );
  };
}

export function getCampaignTotalAddressedMail(
  campaignId: number,
  organisationId: number
): any {
  return function(dispatch, getState) {
    const endpoint = `/campaign/${campaignId}`;
    const options = {
      method: 'GET',
      headers: { token: getState().authentication.token },
      params: {
        productId: config.productId,
        organisationId,
        withSettings: true
      }
    };

    return makeAPICall(endpoint, options).then(
      response => {
        const campaign = response && response.data && response.data.campaign;
        const mdsObject =
          campaign && campaign.details && campaign.details.mdsObject;
        const sa1CountsRaw = mdsObject && mdsObject.SA1Counts;
        const sa1Counts =
          typeof sa1CountsRaw === 'string'
            ? JSON.parse(sa1CountsRaw)
            : sa1CountsRaw;
        const totalAddressedMail =
          sa1Counts && typeof sa1Counts.residential !== 'undefined'
            ? sa1Counts.residential
            : 0;

        return dispatch({
          type: 'UPDATE_CAMPAIGNS_DELIVERY_POINTS',
          deliveryPoints: { deliveryPoints: String(totalAddressedMail) }
        });
      },
      error => dispatch(APIError(error, 'Error getting Campaign'))
    );
  };
}

export function getCampaign(
  campaignId: number,
  organisationId: number,
  withSettings: boolean = true
): any {
  return function(dispatch, getState) {
    const endpoint = `/campaign/${campaignId}`;
    const options = {
      method: 'GET',
      headers: { token: getState().authentication.token },
      params: {
        productId: config.productId,
        organisationId,
        withSettings
      }
    };
    return makeAPICall(endpoint, options).then(
      response => {
        return response.data;
      },
      error => dispatch(APIError(error, 'Error getting Campaign'))
    );
  };
}

export function getCampaignBooking(
  campaignId: number,
  organisationId: number
): any {
  return function(dispatch, getState) {
    const endpoint = `/campaign/${campaignId}/getCampaignBooking`;
    const options = {
      method: 'GET',
      headers: { token: getState().authentication.token },
      params: {
        productId: config.productId,
        organisationId
      }
    };
    return makeAPICall(endpoint, options).then(
      response => {
        return response.data;
      },
      () => {
        return {};
      }
    );
  };
}

export function addCampaign(
  campaign: Object,
  organisationId: number,
  context: string,
  redirect: boolean
): any {
  return function(dispatch, getState) {
    const endpoint = '/campaigns';
    const options = {
      method: 'POST',
      headers: { token: getState().authentication.token },
      params: {
        productId: config.productId,
        organisationId
      },
      data: campaign
    };
    return makeAPICall(endpoint, options).then(
      response => {
        const newCampaignId = response.data.id;
        dispatch(getCampaigns(organisationId));
        dispatch(updateCurrentCampaign(newCampaignId, organisationId));
        if (redirect) {
          redirectTo(
            `/${context}/${organisationId}/campaigns/${newCampaignId}/map`
          );
        }
        return response;
      },
      error => dispatch(APIError(error, 'Error creating Campaign'))
    );
  };
}

export function cloneCampaign(
  campaignId: number,
  organisationId: number,
  context: string
): any {
  return function(dispatch, getState) {
    const endpoint = `/campaign/clone/${campaignId}`;
    const options = {
      method: 'POST',
      headers: { token: getState().authentication.token },
      params: {
        productId: config.productId,
        organisationId
      },
      data: {}
    };
    return makeAPICall(endpoint, options).then(
      response => {
        const newCampaignId = response.data.id;
        dispatch(getCampaigns(organisationId));
        dispatch(updateCurrentCampaign(newCampaignId, organisationId));
        // Redirect to new campaign
        redirectTo(`/${context}/${organisationId}/campaigns/${newCampaignId}`);
        return response;
      },
      error => dispatch(APIError(error, 'Error cloning Campaign'))
    );
  };
}

export function updateCampaign(
  campaign: Object,
  context: string,
  redirect: boolean
): any {
  return function(dispatch, getState) {
    const organisationId = campaign.organisationOwnerId;
    const endpoint = `/campaign/${campaign.id}`;
    const options = {
      method: 'PUT',
      headers: { token: getState().authentication.token },
      params: {
        productId: config.productId,
        organisationId
      },
      data: campaign
    };
    return makeAPICall(endpoint, options).then(
      response => {
        dispatch(getCampaigns(organisationId));
        dispatch(updateCurrentCampaign(campaign.id, organisationId));
        if (redirect) {
          redirectTo(
            `/${context}/${organisationId}/campaigns/${campaign.id}/map`
          );
        }
        return response;
      },
      error => dispatch(APIError(error, 'Error updating Campaign'))
    );
  };
}

export function getCampaignSettings(organisationId: number): any {
  return function(dispatch, getState) {
    const endpoint = '/campaign/settings';
    const options = {
      method: 'GET',
      headers: { token: getState().authentication.token },
      params: {
        productId: config.productId,
        organisationId
      }
    };

    return makeAPICall(endpoint, options).then(
      response =>
        dispatch({
          type: 'UPDATE_CURRENT_CAMPAIGN_SETTINGS',
          settings: response.data
        }),
      error => dispatch(APIError(error, 'Error getting Campaign settings'))
    );
  };
}



// WEBPACK FOOTER //
// ./src/actions/campaigns/basicCreators.js